import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Select from '@cloudscape-design/components/select';
import Table from '@cloudscape-design/components/table';
import { apiJson } from '../utils/api';

interface FeedbackItem {
  story_id: string;
  user_id: string;
  story_kind: string;
  story_key: string;
  rating: string;
  title?: string;
  updated_at?: string;
}

const RATING_OPTIONS = [
  { label: '👎 Thumbs down', value: 'down' },
  { label: '👍 Thumbs up', value: 'up' },
];

const StoryFeedbackPage: React.FC = () => {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rating, setRating] = useState('down');

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiJson<{ items: FeedbackItem[] }>(`/admin/story-feedback?rating=${rating}`);
      setItems(data.items);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [rating]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <ContentLayout
      header={
        <Header variant="h1" description="What people thought of the generated tour-guide stories.">
          Story Feedback
        </Header>
      }
    >
      {error && <Alert type="error">{error}</Alert>}
      <Table
        loading={loading}
        loadingText="Loading feedback"
        items={items}
        variant="full-page"
        columnDefinitions={[
          {
            id: 'title',
            header: 'Aircraft',
            cell: (i) => i.title || i.story_key,
            isRowHeader: true,
          },
          {
            id: 'kind',
            header: 'Story',
            cell: (i) => (i.story_kind === 'airframe' ? 'Specific airframe' : 'Aircraft type'),
          },
          {
            id: 'rating',
            header: 'Rating',
            cell: (i) => (
              <Badge color={i.rating === 'down' ? 'red' : 'green'}>
                {i.rating === 'down' ? '👎 Not helpful' : '👍 Helpful'}
              </Badge>
            ),
          },
          { id: 'key', header: 'Story key', cell: (i) => i.story_key },
          { id: 'when', header: 'When', cell: (i) => i.updated_at?.slice(0, 10) ?? '—' },
        ]}
        filter={
          <Select
            selectedOption={RATING_OPTIONS.find((o) => o.value === rating) ?? RATING_OPTIONS[0]}
            onChange={(e) => setRating(e.detail.selectedOption.value ?? 'down')}
            options={RATING_OPTIONS}
          />
        }
        empty={
          <Box textAlign="center" color="text-body-secondary">
            No {rating === 'down' ? '👎 thumbs-down' : '👍 thumbs-up'} feedback yet.
          </Box>
        }
        header={
          <Header counter={`(${items.length})`}>
            {rating === 'down' ? 'Flagged stories' : 'Liked stories'}
          </Header>
        }
      />
    </ContentLayout>
  );
};

export default StoryFeedbackPage;
